import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CadQuoteLayerPanel from './CadQuoteLayerPanel';

// three.js는 용량이 커서 메인 번들에 묶지 않고, 탭을 열 때만 가져온다.
const CadModelViewer = lazy(() => import('./CadModelViewer'));

/** 자동 추출인지 사람이 고친 값인지 보여주는 작은 칩. */
const SourceChip = ({ touched }) => (
  <Chip
    size="small"
    label={touched ? '수정함' : '자동'}
    color={touched ? 'warning' : 'default'}
    variant={touched ? 'filled' : 'outlined'}
    sx={{ ml: 1 }}
  />
);

const InfoBox = ({ label, value }) => (
  <Box sx={{ textAlign: 'center', p: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{value}</Typography>
  </Box>
);

/**
 * 부품 1종 편집 패널 - 표에서 행을 고르면 이 아래에 그 부품의 수정 가능한 값이 뜬다.
 * 구매품 토글이 켜지면 소재/가공 관련 입력은 숨기고 구매 단가만 보여준다(그
 * 부품은 소재비/가공비를 안 쓰니 - cadQuoteCalc.calcPartCost와 같은 규칙).
 */
const PartEditPanel = ({ part, materials, onFieldChange, onMaterialChange, onMarginChange, onStockAxisChange }) => (
  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2">{part.name} 수정</Typography>
      {part.purchasedGuess && (
        <Chip
          size="small"
          color="info"
          label={`구매품(${part.purchasedReason === '이름' ? '이름' : '형상 추정'})`}
        />
      )}
    </Stack>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="부품명"
          value={part.name}
          onChange={(e) => onFieldChange(part.id, 'name', e.target.value)}
          InputProps={{ endAdornment: <SourceChip touched={part.touched.name} /> }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          size="small"
          label="수량"
          value={part.quantity}
          onChange={(e) => onFieldChange(part.id, 'quantity', Number(e.target.value))}
          InputProps={{ endAdornment: <SourceChip touched={part.touched.quantity} /> }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">구매품(가공 안 함)</Typography>
          <Switch
            size="small"
            checked={part.purchased}
            onChange={(e) => onFieldChange(part.id, 'purchased', e.target.checked)}
          />
          <SourceChip touched={part.touched.purchased} />
        </Stack>
      </Grid>

      {part.purchased ? (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            size="small"
            label="구매 단가(원)"
            value={part.purchasedUnitPrice}
            onChange={(e) => onFieldChange(part.id, 'purchasedUnitPrice', Number(e.target.value))}
            InputProps={{ endAdornment: <SourceChip touched={part.touched.purchasedUnitPrice} /> }}
          />
        </Grid>
      ) : (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              size="small"
              label="재질"
              value={part.material}
              onChange={(e) => onMaterialChange(part.id, e.target.value)}
              InputProps={{ endAdornment: <SourceChip touched={part.touched.material} /> }}
            >
              {Object.keys(materials).map((code) => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label="소재 여유(mm, 각 축 양쪽)"
              value={part.stockMarginMm}
              onChange={(e) => onMarginChange(part.id, e.target.value)}
              InputProps={{ endAdornment: <SourceChip touched={part.touched.stockMarginMm} /> }}
            />
          </Grid>
          {['x', 'y', 'z'].map((axis) => (
            <Grid item xs={12} sm={4} key={axis}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label={`소재 ${axis.toUpperCase()}(mm)`}
                value={part.stockSize[axis]}
                onChange={(e) => onStockAxisChange(part.id, axis, e.target.value)}
                InputProps={{ endAdornment: <SourceChip touched={part.touched[`stock${axis.toUpperCase()}`]} /> }}
              />
            </Grid>
          ))}
        </>
      )}
    </Grid>

    <Grid container spacing={1} sx={{ mt: 1 }}>
      <Grid item xs={6} sm={3}><InfoBox label="부품 부피" value={`${part.volumeCm3?.toFixed(2)} cm³`} /></Grid>
      <Grid item xs={6} sm={3}>
        <InfoBox
          label="최소 R / 공구 힌트"
          value={part.minRadius != null ? `R${part.minRadius} / ${part.toolHint}` : '해당 없음'}
        />
      </Grid>
      <Grid item xs={6} sm={3}><InfoBox label="표면적" value={`${part.areaCm2?.toFixed(2)} cm²`} /></Grid>
      <Grid item xs={6} sm={3}>
        <InfoBox label="구멍" value={(part.holes || []).length ? part.holes.map((h) => `D${h.diameter}×${h.count}`).join(', ') : '없음'} />
      </Grid>
    </Grid>
  </Paper>
);

/**
 * 2단계: cad-service 분석 결과를 사람이 확인/수정한다.
 * 조립품이면 부품별로 나뉜 표가 뜨고, 행을 고르면 3D에서 그 부품만 또렷해지고
 * 아래에 그 부품의 수정 패널이 열린다. 단일 부품 파일도 parts 길이 1이라 같은
 * 화면을 그대로 쓴다(표에 행이 하나뿐일 뿐).
 */
const CadQuoteReviewStep = ({
  analysis, form, materials, onFieldChange,
  selectedPartId, onSelectPart,
  onPartFieldChange, onPartMaterialChange, onPartMarginChange, onPartStockAxisChange,
  onSplit, onCancelSplit, splitLoading,
}) => {
  const [previewTab, setPreviewTab] = useState(0);
  const [explodeFactor, setExplodeFactor] = useState(0); // "벌려보기" 슬라이더(mm)

  // 분석 결과가 바뀌면(새로 올리거나, 레이어로 나누거나, 취소하거나) 벌려보기를
  // 0으로 되돌린다 - 이전 결과의 슬라이더 값이 새 결과에 그대로 남아 있으면
  // "왜 처음부터 벌어져 보이지" 하고 헷갈릴 수 있다.
  useEffect(() => {
    setExplodeFactor(0);
  }, [analysis]);

  if (!analysis) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="text.secondary">먼저 1단계에서 도면을 올려 분석해주세요.</Typography>
      </Paper>
    );
  }

  const parts = form.parts || [];
  const purchasedCount = parts.filter((p) => p.purchased).length;
  const mesh = analysis.mesh;
  const has3dMesh = Boolean(mesh && mesh.parts && mesh.parts.some((p) => p.positions && p.positions.length));
  const activeTab = has3dMesh ? previewTab : 1;
  const selectedPart = parts.find((p) => p.id === selectedPartId) || null;
  const sixViewSvg = selectedPart ? selectedPart.previewSvg : analysis.previewSvg;
  const explodeMax = Math.max(10, Math.round(Math.max(form.bbox?.x || 0, form.bbox?.y || 0, form.bbox?.z || 0)));

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>2. 해석 확인 · 수정</Typography>

      {(analysis.warnings || []).map((warning, index) => (
        <Alert key={index} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>
      ))}

      <Typography variant="body2" sx={{ mb: 1 }}>
        {form.isAssembly ? '조립품으로 분석됨 - ' : ''}
        부품 {parts.length}종 · 총 {form.instanceCount}개 · 구매품 추정 {purchasedCount}종
      </Typography>

      {(has3dMesh || analysis.previewSvg) && (
        <Box sx={{ mb: 2 }}>
          {/* 보기 선택 - "전체"와 부품/레이어 각각이 칩 하나씩. 3D/6면도 두 탭이 같은
              선택을 그대로 쓴다(표 행 클릭, 3D 클릭과도 같은 selectedPartId를 공유). */}
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label="전체(완성품)"
              color={selectedPartId ? 'default' : 'primary'}
              variant={selectedPartId ? 'outlined' : 'filled'}
              onClick={() => onSelectPart(null)}
            />
            {parts.map((part) => (
              <Chip
                key={part.id}
                size="small"
                label={part.name}
                color={selectedPartId === part.id ? 'primary' : 'default'}
                variant={selectedPartId === part.id ? 'filled' : 'outlined'}
                onClick={() => onSelectPart(part.id === selectedPartId ? null : part.id)}
              />
            ))}
          </Stack>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setPreviewTab(value)}
            sx={{ minHeight: 36, mb: 1 }}
          >
            {has3dMesh && <Tab label="3D 돌려보기" sx={{ minHeight: 36, py: 0.5 }} />}
            <Tab label="6면도" sx={{ minHeight: 36, py: 0.5 }} />
          </Tabs>

          {has3dMesh && activeTab === 0 && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}>
              <Suspense
                fallback={(
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 360 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}
              >
                <CadModelViewer
                  mesh={mesh}
                  selectedPartId={selectedPartId}
                  onSelectPart={onSelectPart}
                  explodeFactor={explodeFactor}
                  layered={Boolean(form.layered)}
                  splitAxis={form.splitAxis}
                />
              </Suspense>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 0.5 }}>
                드래그: 회전 · 휠: 확대 · 오른쪽 드래그: 이동 · 부품 클릭: 표에서 선택
              </Typography>
              {parts.length > 1 && (
                <Box sx={{ px: 2, pb: 1 }}>
                  <Typography variant="caption" color="text.secondary">벌려보기</Typography>
                  <Slider
                    size="small"
                    value={explodeFactor}
                    min={0}
                    max={explodeMax}
                    onChange={(_, value) => setExplodeFactor(value)}
                  />
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && sixViewSvg && (
            <Box
              sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: '#fff' }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sixViewSvg }}
            />
          )}
        </Box>
      )}

      {analysis.layerSuggest?.candidates?.length > 0 && (
        <CadQuoteLayerPanel
          layerSuggest={analysis.layerSuggest}
          isLayered={Boolean(form.layered)}
          currentSplitAt={form.splitAt}
          onSplit={onSplit}
          onCancel={onCancelSplit}
          loading={splitLoading}
        />
      )}

      <TextField
        fullWidth
        size="small"
        label="품번(품명)"
        value={form.partNo}
        onChange={(e) => onFieldChange('partNo', e.target.value)}
        sx={{ mb: 2, maxWidth: 360 }}
      />

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>부품</TableCell>
              <TableCell align="right">수량</TableCell>
              <TableCell>구분</TableCell>
              <TableCell align="right">부피(cm³)</TableCell>
              <TableCell>bbox(mm)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parts.map((part) => (
              <TableRow
                key={part.id}
                hover
                selected={part.id === selectedPartId}
                onClick={() => onSelectPart(part.id === selectedPartId ? null : part.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox">
                  {part.id === selectedPartId && <Chip size="small" label="선택" color="primary" />}
                </TableCell>
                <TableCell>{part.name}</TableCell>
                <TableCell align="right">{part.quantity}</TableCell>
                <TableCell>
                  {part.purchased ? (
                    <Chip size="small" color="info" label={`구매품(${part.purchasedReason === '이름' ? '이름' : '형상 추정'})`} />
                  ) : '가공'}
                </TableCell>
                <TableCell align="right">{part.volumeCm3?.toFixed(2)}</TableCell>
                <TableCell>{part.bbox?.x} × {part.bbox?.y} × {part.bbox?.z}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {selectedPart && (
        <PartEditPanel
          part={selectedPart}
          materials={materials}
          onFieldChange={onPartFieldChange}
          onMaterialChange={onPartMaterialChange}
          onMarginChange={onPartMarginChange}
          onStockAxisChange={onPartStockAxisChange}
        />
      )}
      {!selectedPart && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          표에서 부품을 선택하면 수정 항목이 여기에 나타납니다.
        </Typography>
      )}

      <Grid container spacing={1} sx={{ mt: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Grid item xs={6} sm={4}><InfoBox label="전체(완성품) 부피" value={`${analysis.volumeCm3?.toFixed(2)} cm³`} /></Grid>
        <Grid item xs={6} sm={4}><InfoBox label="전체 bbox" value={`${analysis.bbox?.x} × ${analysis.bbox?.y} × ${analysis.bbox?.z} mm`} /></Grid>
        <Grid item xs={6} sm={4}><InfoBox label="전체 표면적" value={`${analysis.areaCm2?.toFixed(2)} cm²`} /></Grid>
      </Grid>
    </Paper>
  );
};

export default CadQuoteReviewStep;
